import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Ship, Building2, Settings2, Battery, Cable, FileText, ChevronRight, ChevronLeft, Check, AlertTriangle, Info, Zap, User, Mail, Phone, Briefcase, Calendar, DollarSign, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { WelcomePage } from "@/components/configurator/WelcomePage";
import { SummaryPage } from "@/components/configurator/SummaryPage";
import { StepDocuments } from "@/components/configurator/StepDocuments";
import { StepRecap } from "@/components/configurator/StepRecap";
import { RouteMap } from "@/components/configurator/RouteMap";
import AdminDashboard from "@/pages/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [lang, setLang] = useState('fr');
  const [t, setT] = useState(null);
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [energyWarn, setEnergyWarn] = useState(false);
  const [contact, setContact] = useState({ company_name: '', contact_name: '', email: '', phone: '', role: '', location: '', project_stage: '' });
  const [projectType, setProjectType] = useState('');
  const [operational, setOperational] = useState({ 
    usage_description: '', 
    trips_per_day: null, 
    trip_duration_minutes: null, 
    trip_distance_nm: null, 
    cruise_speed_knots: null, 
    dock_time_minutes: null,
    // Route map data
    route_data: null
  });
  const [energy, setEnergy] = useState({ 
    shore_charging_possible: null, 
    phase_type: null, // 'single' or 'three'
    amperage: null, // 16, 32, 63, or 'other'
    amperage_other: null, // custom value
    power_unknown: false, 
    charging_window_hours: null, 
    charging_points: null 
  });
  const [technical, setTechnical] = useState({ 
    existing_propulsion: null, 
    engine_power_kw: null, 
    engine_power_unknown: false, 
    constraints: [], 
    hull_type: null,
    boat_length: null,
    target_performance: null, 
    architect_shipyard_status: null,
    // Battery fields for battery_only projects
    batteries_on_board: null,
    current_battery_capacity: null,
    current_battery_power: null
  });
  const [budget, setBudget] = useState({ budget_range: '', decision_timeline: '' });
  const [files, setFiles] = useState([]);
  const totalSteps = 8; // V1.1: Added recap step

  // Handle route map changes
  const handleRouteChange = useCallback((routeData) => {
    setOperational(prev => ({
      ...prev,
      route_data: routeData,
      // Auto-fill trip distance and duration from route if available
      trip_distance_nm: routeData.totalDistance > 0 ? routeData.totalDistance : prev.trip_distance_nm,
      trip_duration_minutes: routeData.totalDuration > 0 ? routeData.totalDuration : prev.trip_duration_minutes,
      // Calculate weighted average speed
      cruise_speed_knots: routeData.totalDistance > 0 && routeData.totalDuration > 0 
        ? Math.round((routeData.totalDistance / (routeData.totalDuration / 60)) * 10) / 10 
        : prev.cruise_speed_knots
    }));
  }, []);

  useEffect(() => { fetch('/translations.json').then(r => r.json()).then(data => setT(data)); }, []);

  useEffect(() => {
    if (!t) return;
    const w = [];
    if (operational.cruise_speed_knots && operational.trip_distance_nm && operational.trip_duration_minutes) {
      const exp = (operational.trip_distance_nm / operational.cruise_speed_knots) * 60;
      if (Math.abs(exp - operational.trip_duration_minutes) > 15) w.push(t[lang].operational.inconsistencySpeed);
    }
    if (operational.dock_time_minutes && operational.dock_time_minutes < 30 && operational.trips_per_day && operational.trips_per_day > 5) {
      w.push(lang === 'fr' ? 'Temps à quai très court avec fréquence élevée' : 'Very short dock time with high frequency');
    }
    setWarnings(w);
  }, [operational, lang, t]);

  useEffect(() => {
    if (operational.trips_per_day && operational.trip_duration_minutes) {
      const hrs = (operational.trips_per_day * operational.trip_duration_minutes) / 60;
      const spd = operational.cruise_speed_knots || 8;
      const pwr = 50 + Math.pow(spd, 2) * 2;
      const daily = hrs * pwr;
      if (energy.shore_charging_possible && energy.charging_window_hours && energy.power_available_kw) {
        setEnergyWarn(energy.power_available_kw * energy.charging_window_hours * 0.85 < daily * 0.8);
      }
    }
  }, [operational, energy]);

  const canProceed = useCallback(() => {
    if (step === 1) return contact.company_name && contact.contact_name && contact.email && contact.phone && contact.role && contact.location && contact.project_stage;
    if (step === 2) return projectType !== '';
    if (step === 3) return operational.usage_description !== '';
    if (step === 4) return energy.shore_charging_possible !== null;
    if (step === 6) return budget.budget_range && budget.decision_timeline;
    return true;
  }, [step, contact, projectType, operational, energy, budget]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const uploaded = [];
      for (const file of files) { const fd = new FormData(); fd.append('file', file); const r = await axios.post(API + '/upload', fd); uploaded.push(r.data.filename); }
      const data = { language: lang, project_type: projectType, contact, operational, energy, technical, budget, uploaded_files: uploaded };
      const res = await axios.post(API + '/projects', data);
      setResult(res.data);
      toast.success(t[lang].common.successSubmit);
    } catch (e) { console.error(e); toast.error(t[lang].common.errorSubmit); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setStarted(false); setStep(1); setResult(null);
    setContact({ company_name: '', contact_name: '', email: '', phone: '', role: '', location: '', project_stage: '' });
    setProjectType(''); setOperational({ usage_description: '', trips_per_day: null, trip_duration_minutes: null, trip_distance_nm: null, cruise_speed_knots: null, dock_time_minutes: null });
    setEnergy({ shore_charging_possible: null, power_available_kw: null, power_unknown: false, charging_window_hours: null, charging_points: null });
    setTechnical({ existing_propulsion: null, engine_power_kw: null, engine_power_unknown: false, constraints: [], hull_type: null, target_performance: null, architect_shipyard_status: null });
    setBudget({ budget_range: '', decision_timeline: '' }); setFiles([]);
  };

  if (!t) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div></div>;
  const tr = t[lang];
  if (!started) return <WelcomePage lang={lang} setLang={setLang} onStart={() => setStarted(true)} tr={tr} />;
  if (result) return <SummaryPage result={result} lang={lang} tr={tr} onReset={reset} />;

  const types = [
    { key: 'retrofit', icon: Settings2, title: tr.projectType.retrofit, desc: tr.projectType.retrofitDesc },
    { key: 'new_build', icon: Ship, title: tr.projectType.newBuild, desc: tr.projectType.newBuildDesc },
    { key: 'hybrid', icon: Zap, title: tr.projectType.hybrid, desc: tr.projectType.hybridDesc },
    { key: 'shore_power', icon: Cable, title: tr.projectType.shorePower, desc: tr.projectType.shorePowerDesc },
    { key: 'battery_only', icon: Battery, title: tr.projectType.batteryOnly, desc: tr.projectType.batteryOnlyDesc },
    { key: 'feasibility', icon: FileText, title: tr.projectType.feasibility, desc: tr.projectType.feasibilityDesc },
  ];

  const renderStep = () => {
    if (step === 1) return (
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
        <h2 className="text-2xl font-bold text-[#0B2A4A] font-['Tomorrow'] mb-2">{tr.identification.title}</h2>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2"><Label>{tr.identification.company} *</Label><div className="relative"><Building2 className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" /><Input className="pl-10" value={contact.company_name} onChange={e => setContact({...contact, company_name: e.target.value})} data-testid="company-input" /></div></div>
          <div className="space-y-2"><Label>{tr.identification.contact} *</Label><div className="relative"><User className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" /><Input className="pl-10" value={contact.contact_name} onChange={e => setContact({...contact, contact_name: e.target.value})} data-testid="contact-input" /></div></div>
          <div className="space-y-2"><Label>{tr.identification.email} *</Label><div className="relative"><Mail className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" /><Input type="email" className="pl-10" value={contact.email} onChange={e => setContact({...contact, email: e.target.value})} data-testid="email-input" /></div></div>
          <div className="space-y-2"><Label>{tr.identification.phone} *</Label><div className="relative"><Phone className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" /><Input className="pl-10" value={contact.phone} onChange={e => setContact({...contact, phone: e.target.value})} data-testid="phone-input" /></div></div>
          <div className="space-y-2"><Label>{tr.identification.role} *</Label><Select value={contact.role} onValueChange={v => setContact({...contact, role: v})}><SelectTrigger data-testid="role-select"><Briefcase className="w-5 h-5 mr-2 text-muted-foreground" /><SelectValue /></SelectTrigger><SelectContent>{Object.entries(tr.roles).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-2"><Label>{tr.identification.location} *</Label><div className="relative"><MapPin className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" /><Input className="pl-10" value={contact.location} onChange={e => setContact({...contact, location: e.target.value})} data-testid="location-input" /></div></div>
          <div className="space-y-2 md:col-span-2"><Label>{tr.identification.projectStage} *</Label><Select value={contact.project_stage} onValueChange={v => setContact({...contact, project_stage: v})}><SelectTrigger data-testid="project-stage-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(tr.stages).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        </div>
      </motion.div>
    );

    if (step === 2) return (
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
        <h2 className="text-2xl font-bold text-[#0B2A4A] font-['Tomorrow']">{tr.projectType.title}</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {types.map(({key, icon: Icon, title, desc}) => (
            <Card key={key} className={`cursor-pointer card-hover ${projectType === key ? 'ring-2 ring-[#0B2A4A] border-[#0B2A4A]' : ''}`} onClick={() => setProjectType(key)} data-testid={`project-type-${key}`}>
              <CardContent className="p-4 flex items-start gap-4">
                <div className={`p-3 rounded-lg ${projectType === key ? 'bg-[#0B2A4A] text-white' : 'bg-muted'}`}><Icon className="w-6 h-6" /></div>
                <div><h3 className="font-semibold text-foreground">{title}</h3><p className="text-sm text-muted-foreground">{desc}</p></div>
                {projectType === key && <Check className="w-5 h-5 text-[#0B2A4A] ml-auto flex-shrink-0" />}
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.div>
    );

    if (step === 3) {
      const showRouteMap = projectType !== 'shore_power';
      return (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
          <div><h2 className="text-2xl font-bold text-[#0B2A4A] font-['Tomorrow'] mb-2">{tr.operational.title}</h2><p className="text-muted-foreground">{tr.operational.description}</p></div>
          <div className="space-y-2"><Label>{tr.operational.usageDescription} *</Label><Textarea rows={3} placeholder={tr.operational.usageDescPlaceholder} value={operational.usage_description} onChange={e => setOperational({...operational, usage_description: e.target.value})} data-testid="usage-description-input" /></div>
          
          {/* Route Map - shown for all types except shore_power */}
          {showRouteMap && (
            <RouteMap 
              value={operational.route_data} 
              onChange={handleRouteChange}
              translations={tr.routeMap}
            />
          )}
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2"><Label>{tr.operational.tripsPerDay}</Label><Input type="number" min="1" value={operational.trips_per_day||''} onChange={e => setOperational({...operational, trips_per_day: e.target.value ? parseInt(e.target.value) : null})} data-testid="trips-per-day-input" /></div>
            <div className="space-y-2"><Label>{tr.operational.tripDuration} {showRouteMap && operational.route_data?.totalDuration > 0 && <span className="text-xs text-green-600">(auto)</span>}</Label><Input type="number" min="1" value={operational.trip_duration_minutes||''} onChange={e => setOperational({...operational, trip_duration_minutes: e.target.value ? parseInt(e.target.value) : null})} data-testid="trip-duration-input" /></div>
            <div className="space-y-2"><Label>{tr.operational.tripDistance} {showRouteMap && operational.route_data?.totalDistance > 0 && <span className="text-xs text-green-600">(auto)</span>}</Label><Input type="number" min="0" step="0.1" value={operational.trip_distance_nm||''} onChange={e => setOperational({...operational, trip_distance_nm: e.target.value ? parseFloat(e.target.value) : null})} data-testid="trip-distance-input" /></div>
            <div className="space-y-2"><Label>{tr.operational.cruiseSpeed} {showRouteMap && operational.route_data?.totalDistance > 0 && <span className="text-xs text-green-600">(moy.)</span>}</Label><Input type="number" min="0" step="0.1" value={operational.cruise_speed_knots||''} onChange={e => setOperational({...operational, cruise_speed_knots: e.target.value ? parseFloat(e.target.value) : null})} data-testid="cruise-speed-input" /></div>
            <div className="space-y-2"><Label>{tr.operational.dockTime}</Label><Input type="number" min="0" value={operational.dock_time_minutes||''} onChange={e => setOperational({...operational, dock_time_minutes: e.target.value ? parseInt(e.target.value) : null})} data-testid="dock-time-input" /></div>
          </div>
          {warnings.length > 0 && <Card className="border-amber-300 bg-amber-50"><CardContent className="p-4"><div className="flex items-start gap-3"><AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" /><div><h4 className="font-semibold text-amber-800">{tr.operational.clarificationNeeded}</h4><p className="text-sm text-amber-700 mt-1">{tr.operational.clarificationMessage}</p><ul className="mt-2 space-y-1">{warnings.map((w,i) => <li key={i} className="text-sm text-amber-700">• {w}</li>)}</ul></div></div></CardContent></Card>}
        </motion.div>
      );
    }

    if (step === 4) return (
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
        <h2 className="text-2xl font-bold text-[#0B2A4A] font-['Tomorrow']">{tr.energy.title}</h2>
        <div className="space-y-4"><Label>{tr.energy.shoreCharging} *</Label>
          <RadioGroup value={energy.shore_charging_possible === true ? 'yes' : energy.shore_charging_possible === false ? 'no' : energy.shore_charging_possible === 'unknown' ? 'unknown' : ''} onValueChange={v => setEnergy({...energy, shore_charging_possible: v === 'yes' ? true : v === 'no' ? false : v === 'unknown' ? 'unknown' : null})} className="flex gap-4">
            <div className="flex items-center space-x-2"><RadioGroupItem value="yes" id="shore-yes" data-testid="shore-charging-yes" /><Label htmlFor="shore-yes">{tr.energy.yes}</Label></div>
            <div className="flex items-center space-x-2"><RadioGroupItem value="no" id="shore-no" data-testid="shore-charging-no" /><Label htmlFor="shore-no">{tr.energy.no}</Label></div>
            <div className="flex items-center space-x-2"><RadioGroupItem value="unknown" id="shore-unknown" data-testid="shore-charging-unknown" /><Label htmlFor="shore-unknown">{tr.energy.unknown}</Label></div>
          </RadioGroup>
        </div>
        {energy.shore_charging_possible === true && <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-6">
          {/* Phase type */}
          <div className="space-y-3">
            <Label>{tr.energy.phaseType} *</Label>
            <RadioGroup value={energy.phase_type || ''} onValueChange={v => setEnergy({...energy, phase_type: v})} className="flex gap-6">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="single" id="phase-single" data-testid="phase-single" />
                <Label htmlFor="phase-single">{tr.energy.singlePhase}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="three" id="phase-three" data-testid="phase-three" />
                <Label htmlFor="phase-three">{tr.energy.threePhase}</Label>
              </div>
            </RadioGroup>
          </div>
          
          {/* Amperage selection */}
          <div className="space-y-3">
            <Label>{tr.energy.amperageAvailable} *</Label>
            <div className="flex flex-wrap gap-3">
              {[16, 32, 63].map(amp => (
                <Button
                  key={amp}
                  type="button"
                  variant={energy.amperage === amp ? "default" : "outline"}
                  className={energy.amperage === amp ? "bg-[#0B2A4A]" : ""}
                  onClick={() => setEnergy({...energy, amperage: amp, amperage_other: null})}
                  data-testid={`amperage-${amp}`}
                >
                  {amp} A
                </Button>
              ))}
              <Button
                type="button"
                variant={energy.amperage === 'other' ? "default" : "outline"}
                className={energy.amperage === 'other' ? "bg-[#0B2A4A]" : ""}
                onClick={() => setEnergy({...energy, amperage: 'other'})}
                data-testid="amperage-other"
              >
                {tr.energy.other}
              </Button>
            </div>
            {energy.amperage === 'other' && (
              <div className="mt-2">
                <Input 
                  type="number" 
                  min="0" 
                  placeholder={tr.energy.otherAmperagePlaceholder}
                  value={energy.amperage_other || ''} 
                  onChange={e => setEnergy({...energy, amperage_other: e.target.value ? parseFloat(e.target.value) : null})} 
                  className="max-w-[200px]"
                  data-testid="amperage-other-input" 
                />
              </div>
            )}
            <div className="flex items-center gap-2 mt-2">
              <Checkbox id="power-unknown" checked={energy.power_unknown} onCheckedChange={c => setEnergy({...energy, power_unknown: c, amperage: c ? null : energy.amperage, amperage_other: c ? null : energy.amperage_other})} data-testid="power-unknown-checkbox" />
              <Label htmlFor="power-unknown" className="text-sm">{tr.energy.powerUnknown}</Label>
            </div>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2"><Label>{tr.energy.chargingWindow}</Label><Input type="number" min="0" step="0.5" value={energy.charging_window_hours||''} onChange={e => setEnergy({...energy, charging_window_hours: e.target.value ? parseFloat(e.target.value) : null})} data-testid="charging-window-input" /></div>
            <div className="space-y-2"><Label>{tr.energy.chargingPoints}</Label><Input type="number" min="1" value={energy.charging_points||''} onChange={e => setEnergy({...energy, charging_points: e.target.value ? parseInt(e.target.value) : null})} data-testid="charging-points-input" /></div>
          </div>
        </motion.div>}
        {energyWarn && <Card className="border-blue-300 bg-blue-50"><CardContent className="p-4"><div className="flex items-start gap-3"><Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" /><p className="text-sm text-blue-700">{tr.energy.warning}</p></div></CardContent></Card>}
      </motion.div>
    );

    if (step === 5) {
      const isRetrofit = projectType === 'retrofit' || projectType === 'hybrid';
      const isBatteryOnly = projectType === 'battery_only';
      const showHullType = ['retrofit', 'hybrid', 'battery_only', 'feasibility'].includes(projectType);
      const showBoatLength = ['retrofit', 'hybrid', 'battery_only', 'feasibility', 'new_build'].includes(projectType);
      
      return (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
          <h2 className="text-2xl font-bold text-[#0B2A4A] font-['Tomorrow']">{tr.technical.title}</h2>
          
          {/* Boat length for retrofit, hybrid, battery_only, feasibility, new_build */}
          {showBoatLength && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label>{tr.technical.boatLength}</Label>
                <Input 
                  type="number" 
                  min="0" 
                  step="0.1"
                  value={technical.boat_length||''} 
                  onChange={e => setTechnical({...technical, boat_length: e.target.value ? parseFloat(e.target.value) : null})}
                  data-testid="boat-length-input"
                />
              </div>
              {/* Hull type next to boat length for types that need it */}
              {showHullType && (
                <div className="space-y-2">
                  <Label>{tr.technical.hullType}</Label>
                  <Select value={technical.hull_type||''} onValueChange={v => setTechnical({...technical, hull_type: v})}>
                    <SelectTrigger data-testid="hull-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(tr.hullTypes).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* Battery only specific fields */}
          {isBatteryOnly && (
            <div className="space-y-6 p-4 bg-muted/50 rounded-lg">
              <div className="space-y-4">
                <Label className="text-base font-semibold">{tr.technical.batteriesOnBoard}</Label>
                <RadioGroup 
                  value={technical.batteries_on_board === true ? 'yes' : technical.batteries_on_board === false ? 'no' : ''} 
                  onValueChange={v => setTechnical({...technical, batteries_on_board: v === 'yes' ? true : v === 'no' ? false : null})} 
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="batteries-yes" data-testid="batteries-on-board-yes" />
                    <Label htmlFor="batteries-yes">{tr.technical.batteriesOnBoardYes}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="batteries-no" data-testid="batteries-on-board-no" />
                    <Label htmlFor="batteries-no">{tr.technical.batteriesOnBoardNo}</Label>
                  </div>
                </RadioGroup>
              </div>
              
              {technical.batteries_on_board === true && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{tr.technical.currentBatteryCapacity}</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      value={technical.current_battery_capacity||''} 
                      onChange={e => setTechnical({...technical, current_battery_capacity: e.target.value ? parseFloat(e.target.value) : null})} 
                      data-testid="current-battery-capacity-input" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{tr.technical.currentBatteryPower}</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      value={technical.current_battery_power||''} 
                      onChange={e => setTechnical({...technical, current_battery_power: e.target.value ? parseFloat(e.target.value) : null})} 
                      data-testid="current-battery-power-input" 
                    />
                  </div>
                </motion.div>
              )}
            </div>
          )}

          {/* Retrofit/Hybrid specific fields */}
          {isRetrofit && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2"><Label>{tr.technical.existingPropulsion}</Label><Select value={technical.existing_propulsion||''} onValueChange={v => setTechnical({...technical, existing_propulsion: v})}><SelectTrigger data-testid="propulsion-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(tr.propulsionTypes).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>{tr.technical.enginePower}</Label><Input type="number" min="0" disabled={technical.engine_power_unknown} value={technical.engine_power_kw||''} onChange={e => setTechnical({...technical, engine_power_kw: e.target.value ? parseFloat(e.target.value) : null})} data-testid="engine-power-input" /><div className="flex items-center gap-2"><Checkbox id="engine-unknown" checked={technical.engine_power_unknown} onCheckedChange={c => setTechnical({...technical, engine_power_unknown: c})} data-testid="engine-power-unknown-checkbox" /><Label htmlFor="engine-unknown" className="text-sm">{tr.technical.enginePowerUnknown}</Label></div></div>
              <div className="space-y-3 md:col-span-2"><Label>{tr.technical.constraints}</Label><div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2"><Checkbox checked={technical.constraints?.includes('space')} onCheckedChange={c => setTechnical({...technical, constraints: c ? [...(technical.constraints||[]), 'space'] : (technical.constraints||[]).filter(x=>x!=='space')})} data-testid="constraint-space-checkbox" /><Label>{tr.technical.constraintSpace}</Label></div>
                <div className="flex items-center gap-2"><Checkbox checked={technical.constraints?.includes('weight')} onCheckedChange={c => setTechnical({...technical, constraints: c ? [...(technical.constraints||[]), 'weight'] : (technical.constraints||[]).filter(x=>x!=='weight')})} data-testid="constraint-weight-checkbox" /><Label>{tr.technical.constraintWeight}</Label></div>
                <div className="flex items-center gap-2"><Checkbox checked={technical.constraints?.includes('noise')} onCheckedChange={c => setTechnical({...technical, constraints: c ? [...(technical.constraints||[]), 'noise'] : (technical.constraints||[]).filter(x=>x!=='noise')})} data-testid="constraint-noise-checkbox" /><Label>{tr.technical.constraintNoise}</Label></div>
                <div className="flex items-center gap-2"><Checkbox checked={technical.constraints?.includes('vibration')} onCheckedChange={c => setTechnical({...technical, constraints: c ? [...(technical.constraints||[]), 'vibration'] : (technical.constraints||[]).filter(x=>x!=='vibration')})} data-testid="constraint-vibration-checkbox" /><Label>{tr.technical.constraintVibration}</Label></div>
              </div></div>
            </div>
          )}

          {/* New build specific fields */}
          {projectType === 'new_build' && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2"><Label>{tr.technical.hullType}</Label><Select value={technical.hull_type||''} onValueChange={v => setTechnical({...technical, hull_type: v})}><SelectTrigger data-testid="hull-type-select-newbuild"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(tr.hullTypes).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>{tr.technical.targetPerformance}</Label><Textarea rows={2} value={technical.target_performance||''} onChange={e => setTechnical({...technical, target_performance: e.target.value})} data-testid="target-performance-input" /></div>
              <div className="space-y-2 md:col-span-2"><Label>{tr.technical.architectStatus}</Label><Input value={technical.architect_shipyard_status||''} onChange={e => setTechnical({...technical, architect_shipyard_status: e.target.value})} data-testid="architect-status-input" /></div>
            </div>
          )}

          {/* Shore power - minimal fields */}
          {projectType === 'shore_power' && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2"><Label>{tr.technical.targetPerformance}</Label><Textarea rows={3} value={technical.target_performance||''} onChange={e => setTechnical({...technical, target_performance: e.target.value})} placeholder="Describe the shore power infrastructure requirements..." data-testid="target-performance-input" /></div>
            </div>
          )}

          {/* Feasibility study */}
          {projectType === 'feasibility' && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2"><Label>{tr.technical.targetPerformance}</Label><Textarea rows={3} value={technical.target_performance||''} onChange={e => setTechnical({...technical, target_performance: e.target.value})} placeholder="Describe the scope and objectives of the feasibility study..." data-testid="target-performance-input" /></div>
            </div>
          )}
        </motion.div>
      );
    }

    if (step === 6) return (
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
        <h2 className="text-2xl font-bold text-[#0B2A4A] font-['Tomorrow']">{tr.budget.title}</h2>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2"><Label>{tr.budget.budgetRange} *</Label><Select value={budget.budget_range} onValueChange={v => setBudget({...budget, budget_range: v})}><SelectTrigger data-testid="budget-range-select"><DollarSign className="w-5 h-5 mr-2 text-muted-foreground" /><SelectValue /></SelectTrigger><SelectContent>{Object.entries(tr.budgets).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-2"><Label>{tr.budget.timeline} *</Label><Select value={budget.decision_timeline} onValueChange={v => setBudget({...budget, decision_timeline: v})}><SelectTrigger data-testid="timeline-select"><Calendar className="w-5 h-5 mr-2 text-muted-foreground" /><SelectValue /></SelectTrigger><SelectContent>{Object.entries(tr.timelines).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        </div>
      </motion.div>
    );

    // V1.1: New Recap Step (Step 7)
    if (step === 7) return (
      <StepRecap
        tr={tr}
        projectType={projectType}
        contact={contact}
        operational={operational}
        energy={energy}
        technical={technical}
        budget={budget}
        onConfirm={() => setStep(8)}
        onEdit={(targetStep) => setStep(targetStep)}
      />
    );

    // Documents is now Step 8
    if (step === 8) return <StepDocuments tr={tr} files={files} setFiles={setFiles} />;

    return null;
  };

  // Hide navigation on recap step (it has its own buttons)
  const showNavigation = step !== 7;

  return (
    <div className="min-h-screen maritime-gradient">
      <Toaster position="top-right" richColors />
      <header className="w-full py-4 px-6 bg-[#0B2A4A] border-b flex justify-between items-center">
        <div className="flex items-center gap-3"><img src="/logo-switch.png" alt="Switch" className="h-8" /></div>
        <div className="lang-toggle"><button className={`lang-btn ${lang === 'fr' ? 'active' : ''}`} onClick={() => setLang('fr')} data-testid="wizard-lang-fr-btn"><span className="mr-1">🇫🇷</span>FR</button><button className={`lang-btn ${lang === 'en' ? 'active' : ''}`} onClick={() => setLang('en')} data-testid="wizard-lang-en-btn"><span className="mr-1">🇬🇧</span>EN</button></div>
      </header>
      <main className="container max-w-7xl mx-auto px-4 py-8">
        <div className="lg:hidden mb-6"><div className="flex items-center justify-between mb-2"><span className="text-sm font-medium text-muted-foreground">Step {step} of {totalSteps}</span><span className="text-sm font-medium text-[#0B2A4A]">{Math.round((step / totalSteps) * 100)}%</span></div><Progress value={(step / totalSteps) * 100} className="h-2" /></div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-8 bg-white rounded-lg border shadow-sm p-4">
              <div className="space-y-1">
                {Object.entries(tr.steps).map(([key, name], index) => {
                  const n = index + 1, active = step === n, done = step > n;
                  return <div key={key} className={`stepper-item flex items-center gap-3 p-3 rounded-md ${active ? 'active' : ''} ${done ? 'completed' : ''}`} data-testid={`step-${n}-indicator`}><div className={`stepper-circle ${active ? 'active' : ''} ${done ? 'completed' : ''}`}>{done ? <Check className="w-4 h-4" /> : n}</div><span className={`text-sm font-medium ${active ? 'text-[#0B2A4A]' : ''}`}>{name}</span></div>;
                })}
              </div>
            </div>
          </div>
          <div className="lg:col-span-9">
            <Card><CardContent className="p-6 md:p-8">
              <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
              {showNavigation && (
                <div className="flex justify-between mt-8 pt-6 border-t">
                  <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} data-testid="previous-step-btn"><ChevronLeft className="w-4 h-4 mr-2" />{tr.common.previous}</Button>
                  {step === totalSteps ? (
                    <Button onClick={handleSubmit} disabled={!canProceed() || submitting} className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90" data-testid="submit-project-btn">
                      {submitting ? <span className="flex items-center gap-2"><span className="animate-spin">⏳</span>{tr.common.submitting}</span> : <span className="flex items-center gap-2">{tr.common.submit}<Check className="w-4 h-4" /></span>}
                    </Button>
                  ) : (
                    <Button onClick={() => setStep(Math.min(totalSteps, step + 1))} disabled={!canProceed()} className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90" data-testid="next-step-btn">{tr.common.next}<ChevronRight className="w-4 h-4 ml-2" /></Button>
                  )}
                </div>
              )}
            </CardContent></Card>
          </div>
        </div>
      </main>
    </div>
  );
}

const AppWrapper = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<App />} />
      <Route path="/admin" element={<AdminDashboard />} />
    </Routes>
  </BrowserRouter>
);

export default AppWrapper;
